import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  FileText,
  FileUp,
  Handshake,
  Info,
  Loader2,
  RefreshCw,
  Target,
  Upload,
  XCircle,
} from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import AppNav from "@/components/app/AppNav";
import CreditReportCTA from "@/components/app/CreditReportCTA";

const STATUS_META = {
  eligible: {
    label: "Eligible now",
    icon: CheckCircle2,
    color: "var(--ds-color-success)",
    soft: "var(--ds-color-success-soft)",
    border: "var(--ds-color-success-soft)",
  },
  almost_eligible: {
    label: "Almost eligible",
    icon: Target,
    color: "var(--ds-color-accent)",
    soft: "var(--ds-color-accent-soft)",
    border: "var(--ds-color-accent-soft)",
  },
  not_eligible: {
    label: "Not yet eligible",
    icon: XCircle,
    color: "var(--ds-color-ink-muted)",
    soft: "var(--ds-color-surface-muted)",
    border: "var(--ds-color-surface-muted)",
  },
};

const TAB_KEYS = ["all", "eligible", "almost_eligible", "not_eligible"];
const TAB_LABEL = {
  all: "All",
  eligible: "Eligible",
  almost_eligible: "Almost eligible",
  not_eligible: "Not yet",
};

export default function Partners() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("all");

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((res) => {
        if (!cancelled) setSiteSettings(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user?.funding_profile_complete) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get("/partners")
      .then((res) => {
        if (!cancelled) setResult(res.data);
      })
      .catch((err) => {
        if (!cancelled) setError(formatApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.funding_profile_complete]);

  async function recompute() {
    setRecomputing(true);
    setError("");
    try {
      const res = await api.post("/partners/recompute");
      setResult(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setRecomputing(false);
    }
  }

  const matches = useMemo(() => result?.matches || [], [result]);
  const summary = result?.summary || { eligible_count: 0, almost_count: 0, not_eligible_count: 0 };
  const visible = useMemo(() => {
    if (tab === "all") return matches;
    return matches.filter((m) => m.status === tab);
  }, [tab, matches]);

  const showNoCreditReportHint =
    user?.funding_profile_complete && result && !result.inputs_snapshot?.credit_score;

  if (!user?.funding_profile_complete) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <EmptyState
          title="Funding partner match"
          subtitle="Complete your profile and (optionally) upload a credit report to see your funding partner matches. We'll match you against active lenders based on your credit, revenue, and business foundation."
          primaryLabel="Finish my profile"
          onPrimary={() => navigate("/profile")}
        />
      </Shell>
    );
  }

  if (loading) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <div data-testid="partners-loading" className="flex items-center justify-center min-h-[40vh]">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--ds-color-accent)]" />
        </div>
      </Shell>
    );
  }

  if (!matches.length) {
    return (
      <Shell disclaimer={siteSettings?.disclaimer_text}>
        <EmptyState
          title="No funding partners are active yet"
          subtitle="Your match will populate here as soon as funding partners are activated. Strengthen your profile in the meantime."
          primaryLabel="Upload credit report"
          onPrimary={() => navigate("/credit-report")}
          error={error}
        />
      </Shell>
    );
  }

  const computedAt = new Date(result.computed_at);

  return (
    <Shell disclaimer={siteSettings?.disclaimer_text}>
      {/* HERO */}
      <section
        data-testid="partners-hero"
        className="rounded-[var(--ds-radius-card)] p-7 md:p-10 mt-6"
        style={{
          background:
            "var(--ds-color-accent-soft)",
          border: "1px solid var(--ds-color-border)",
        }}
      >
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">
              Funding partner match
            </div>
            <h1
              data-testid="partners-title"
              className="font-semibold tracking-tight mt-2 text-[30px] md:text-[36px] font-semibold leading-tight tracking-tight"
            >
              Where you stand with active lenders
            </h1>
            <p
              data-testid="partners-page-subtitle"
              className="mt-2 text-sm text-[var(--ds-color-ink-muted)] leading-relaxed max-w-2xl"
            >
              Match with vetted lenders based on your live financial profile.
            </p>
            <p className="mt-3 text-sm text-[var(--ds-color-ink-muted)] max-w-2xl leading-relaxed">
              Educational match based on your profile, readiness, and (when present) extracted credit
              report. Partners make their own approval decisions — we don't guarantee funding.
            </p>
            <p className="mt-3 text-xs text-[var(--ds-color-ink-muted)]">
              Last computed: {computedAt.toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            data-testid="partners-recompute-btn"
            onClick={recompute}
            disabled={recomputing}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] transition-all duration-300 disabled:opacity-60"
          >
            {recomputing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {recomputing ? "Recomputing…" : "Recompute"}
          </button>
        </div>
        {error && (
          <p data-testid="partners-error" className="mt-4 text-sm" style={{ color: "var(--ds-color-danger)" }}>{error}</p>
        )}
      </section>

      {/* SUMMARY TILES */}
      <section data-testid="partners-summary" className="mt-6 grid grid-cols-3 gap-3">
        <SummaryTile
          label="Eligible"
          value={summary.eligible_count}
          color={STATUS_META.eligible.color}
          testId="partners-summary-eligible"
        />
        <SummaryTile
          label="Almost"
          value={summary.almost_count}
          color={STATUS_META.almost_eligible.color}
          testId="partners-summary-almost"
        />
        <SummaryTile
          label="Not yet"
          value={summary.not_eligible_count}
          color={STATUS_META.not_eligible.color}
          testId="partners-summary-not"
        />
      </section>

      {showNoCreditReportHint && (
        <div
          data-testid="partners-credit-hint"
          className="mt-5 rounded-[var(--ds-radius-card)] p-5"
          style={{
            background: "var(--ds-color-accent-soft)",
            border: "1px solid var(--ds-color-accent-soft)",
          }}
        >
          <div className="flex gap-3">
            <FileUp className="w-4 h-4 mt-0.5 text-[var(--ds-color-accent)] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-[var(--ds-color-ink-muted)] leading-relaxed">
                Upload a credit report to refine these matches with your actual score and payment
                history instead of your self-reported estimates.
              </p>
              <button
                type="button"
                onClick={() => navigate("/credit-report")}
                data-testid="partners-credit-hint-btn"
                className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-[var(--ds-color-accent)] hover:underline"
              >
                Upload credit report <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 6 a11y — the tab list here is semantically correct
          (role="tablist"), but a `<nav>` landmark cannot also be a
          tablist. Use a plain `<div>` and keep role="tablist" so screen
          readers announce the tab pattern without duplicating the
          navigation landmark. */}
      <div
        data-testid="partners-tabs"
        role="tablist"
        className="mt-8 inline-flex items-center gap-1 rounded-full p-1"
        style={{
          background: "var(--ds-color-surface-muted)",
          border: "1px solid var(--ds-color-border)",
        }}
      >
        {TAB_KEYS.map((key) => {
          const active = tab === key;
          const count =
            key === "all"
              ? matches.length
              : key === "eligible"
                ? summary.eligible_count
                : key === "almost_eligible"
                  ? summary.almost_count
                  : summary.not_eligible_count;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={`partners-tab-${key}`}
              onClick={() => setTab(key)}
              className="px-4 py-2 rounded-full text-xs uppercase tracking-[0.18em] font-medium transition-all"
              style={{
                background: active ? "var(--ds-color-accent)" : "transparent",
                color: active ? "var(--ds-color-background)" : "var(--ds-color-ink-muted)",
              }}
            >
              {TAB_LABEL[key]} <span className="ml-1 opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      {/* CARDS */}
      <section data-testid="partners-cards" className="mt-6 space-y-4">
        {visible.length === 0 ? (
          <p data-testid="partners-tab-empty" className="text-sm text-[var(--ds-color-ink-muted)]">
            No partners in this tab right now.
          </p>
        ) : tab === "all" ? (
          <GroupedAllView matches={visible} />
        ) : (
          visible.map((m) => <PartnerCard key={m.partner_id} match={m} />)
        )}
      </section>
    </Shell>
  );
}

function GroupedAllView({ matches }) {
  const groups = [
    { key: "eligible", title: "Eligible now" },
    { key: "almost_eligible", title: "Almost eligible" },
    { key: "not_eligible", title: "Not yet eligible" },
  ];
  return (
    <div className="space-y-8">
      {groups.map((g) => {
        const items = matches.filter((m) => m.status === g.key);
        if (items.length === 0) return null;
        return (
          <div key={g.key} data-testid={`partners-group-${g.key}`}>
            <div
              className="flex items-center gap-3 mb-3 pb-2"
              style={{ borderBottom: "1px solid var(--ds-color-border)" }}
            >
              <span
                className="text-[11px] uppercase tracking-[0.18em] font-medium"
                style={{ color: STATUS_META[g.key].color }}
              >
                {g.title}
              </span>
              <span className="text-[11px] text-[var(--ds-color-ink-muted)]">{items.length}</span>
            </div>
            <div className="space-y-4">
              {items.map((m) => (
                <PartnerCard key={m.partner_id} match={m} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PartnerCard({ match }) {
  const meta = STATUS_META[match.status] || STATUS_META.not_eligible;
  const Icon = meta.icon;
  const [open, setOpen] = useState(match.status !== "not_eligible");

  async function handleApply() {
    try {
      await api.post(`/partners/${match.partner_id}/track-click`, { source_page: "partners" });
    } catch {
      // Telemetry is fire-and-forget — never block the outbound nav.
    }
    window.open(match.application_link, "_blank", "noopener,noreferrer");
  }

  return (
    <article
      data-testid={`partner-card-${match.partner_id}`}
      data-status={match.status}
      className="rounded-[var(--ds-radius-card)] p-6 md:p-7"
      style={{ background: meta.soft, border: `1px solid ${meta.border}` }}
    >
      <header className="flex items-start gap-4 flex-wrap">
        <div
          className="inline-flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0"
          style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}
        >
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap">
            <h3
              data-testid={`partner-name-${match.partner_id}`}
              className="font-semibold tracking-tight text-lg md:text-xl font-medium text-[var(--ds-color-ink)]"
            >
              {match.partner_name}
            </h3>
            <span
              data-testid={`partner-status-${match.partner_id}`}
              className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full"
              style={{
                background: `${meta.color}1f`,
                border: `1px solid ${meta.color}55`,
                color: meta.color,
              }}
            >
              {meta.label}
            </span>
            {match.funding_range_label && (
              <span
                data-testid={`partner-range-${match.partner_id}`}
                className="text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full text-[var(--ds-color-ink-muted)]"
                style={{
                  background: "var(--ds-color-surface-muted)",
                  border: "1px solid var(--ds-color-border)",
                }}
              >
                {match.funding_range_label}
              </span>
            )}
          </div>
          {match.description && (
            <p className="mt-2 text-sm text-[var(--ds-color-ink-muted)] leading-relaxed">
              {match.description}
            </p>
          )}
        </div>
        {match.status === "eligible" && (
          <button
            type="button"
            onClick={handleApply}
            data-testid={`partner-apply-${match.partner_id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] transition-all duration-300"
          >
            Apply now <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
        {match.status === "almost_eligible" && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={`partner-improve-${match.partner_id}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold"
            style={{
              background: "transparent",
              border: `1px solid ${meta.color}`,
              color: meta.color,
            }}
          >
            How to qualify <ChevronDown className="w-3.5 h-3.5" />
          </button>
        )}
        {match.status === "not_eligible" && (
          <span
            className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[var(--ds-color-ink-muted)]"
            style={{
              background: "var(--ds-color-surface-muted)",
              border: "1px solid var(--ds-color-border)",
            }}
          >
            Improve to qualify
          </span>
        )}
      </header>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`partner-toggle-${match.partner_id}`}
        className="mt-5 inline-flex items-center gap-1 text-xs text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] transition-colors"
      >
        {open ? "Hide details" : "View details"}
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>

      {open && (
        <div data-testid={`partner-details-${match.partner_id}`} className="mt-4 grid md:grid-cols-2 gap-5">
          {match.reasons_qualified.length > 0 && (
            <ReasonList
              testId={`partner-reasons-qualified-${match.partner_id}`}
              title="Why you qualify"
              icon={CheckCircle2}
              color="var(--ds-color-success)"
              items={match.reasons_qualified}
            />
          )}
          {match.reasons_not_qualified.length > 0 && (
            <ReasonList
              testId={`partner-reasons-not-qualified-${match.partner_id}`}
              title="What's holding you back"
              icon={AlertCircle}
              color="var(--ds-color-warning)"
              items={match.reasons_not_qualified}
            />
          )}
          {match.improvement_actions.length > 0 && (
            <ReasonList
              testId={`partner-improvements-${match.partner_id}`}
              title="How to qualify"
              icon={Target}
              color="var(--ds-color-accent)"
              items={match.improvement_actions}
            />
          )}
          {match.documents_needed.length > 0 && (
            <ReasonList
              testId={`partner-documents-${match.partner_id}`}
              title="Documents you'll need"
              icon={Info}
              color="var(--ds-color-accent)"
              items={match.documents_needed}
            />
          )}
          {match.documents_status && (
            <BankStatementsStatusPanel
              partnerId={match.partner_id}
              docs={match.documents_status}
            />
          )}
          {match.qualification_tips && match.qualification_tips.length > 0 && (
            <div data-testid={`partner-tips-${match.partner_id}`} className="md:col-span-2">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-3.5 h-3.5" style={{ color: "var(--ds-color-accent)" }} />
                <span className="text-[10px] uppercase tracking-[0.18em] font-medium" style={{ color: "var(--ds-color-accent)" }}>
                  Tips to improve approval odds
                </span>
              </div>
              <ul className="space-y-2 text-sm text-[var(--ds-color-ink-muted)] leading-relaxed">
                {match.qualification_tips.map((tip, i) => (
                  <li key={i} className="flex gap-2 items-start">
                    <span className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0" style={{ background: "var(--ds-color-accent)" }} />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {match.improvement_actions.some(a => /credit/i.test(a)) && (
            <div data-testid={`partner-credit-cta-${match.partner_id}`} className="md:col-span-2 mt-1">
              <CreditReportCTA testid={`partner-${match.partner_id}-credit-cta`} />
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function BankStatementsStatusPanel({ partnerId, docs }) {
  const required = docs.bank_statements_required ?? 3;
  const uploaded = docs.bank_statements_uploaded ?? 0;
  const remaining = docs.bank_statements_remaining ?? Math.max(required - uploaded, 0);
  const ready = docs.bank_statements_ready ?? uploaded >= required;
  const pct = Math.min(100, Math.round((uploaded / Math.max(required, 1)) * 100));
  return (
    <div
      data-testid={`partner-bank-statements-${partnerId}`}
      className="md:col-span-2 rounded-xl p-4"
      style={{
        background: ready ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
        border: `1px solid ${ready ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)"}`,
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-3.5 h-3.5" style={{ color: ready ? "var(--ds-color-success)" : "var(--ds-color-warning)" }} />
          <span
            className="text-[10px] uppercase tracking-[0.18em] font-medium"
            style={{ color: ready ? "var(--ds-color-success)" : "var(--ds-color-warning)" }}
          >
            Bank statements
          </span>
          <span
            data-testid={`partner-bank-statements-count-${partnerId}`}
            className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full"
            style={{
              background: ready ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)",
              border: `1px solid ${ready ? "var(--ds-color-success-soft)" : "var(--ds-color-warning-soft)"}`,
              color: ready ? "var(--ds-color-success)" : "var(--ds-color-warning)",
            }}
          >
            {ready ? (
              <>
                <CheckCircle2 className="inline w-3 h-3 mr-1" /> Ready
              </>
            ) : (
              `${uploaded}/${required}`
            )}
          </span>
        </div>
        {!ready && (
          <Link
            to="/bank-statements"
            data-testid={`partner-bank-statements-cta-${partnerId}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold hover:underline"
            style={{ color: "var(--ds-color-warning)" }}
          >
            <Upload className="w-3 h-3" />
            {remaining === 1 ? "Upload 1 more" : `Upload ${remaining} more`}
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      <div className="mt-3 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--ds-color-surface-muted)" }}>
        <div
          data-testid={`partner-bank-statements-progress-${partnerId}`}
          className="h-full transition-all"
          style={{
            width: `${pct}%`,
            background: ready ? "var(--ds-color-success)" : "var(--ds-color-warning)",
          }}
        />
      </div>
      <p className="mt-2 text-xs text-[var(--ds-color-ink-muted)] leading-relaxed">
        {ready
          ? `You've uploaded ${uploaded} statement${uploaded === 1 ? "" : "s"} — this partner's bank-statement requirement is met.`
          : `This partner requires ${required} months of business bank statements. You've uploaded ${uploaded}.`}
      </p>
    </div>
  );
}

function ReasonList({ title, icon: Icon, color, items, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span
          className="text-[10px] uppercase tracking-[0.18em] font-medium"
          style={{ color }}
        >
          {title}
        </span>
      </div>
      <ul className="space-y-2 text-sm text-[var(--ds-color-ink-muted)] leading-relaxed">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 items-start">
            <span
              className="mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
              style={{ background: color }}
            />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SummaryTile({ label, value, color, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-[var(--ds-radius-card)] p-5 text-center"
      style={{
        background: "var(--ds-color-surface)",
        border: `1px solid ${color}33`,
      }}
    >
      <div className="font-semibold tracking-tight text-[30px] font-semibold" style={{ color }}>{value}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[var(--ds-color-ink-muted)]">{label}</div>
    </div>
  );
}

function Shell({ children, disclaimer }) {
  return (
    <div
      data-testid="partners-root"
      data-page-outer="cluster-b"
      className="min-h-screen text-[var(--ds-color-ink)] bg-[var(--ds-color-background)]"
      style={{ background: "var(--ds-color-background)" }}
    >
      <AppNav />
      <main className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-24">
        {disclaimer && (
          <div
            data-testid="partners-disclaimer"
            className="rounded-[var(--ds-radius-card)] p-5"
            style={{
              background: "var(--ds-color-accent-soft)",
              border: "1px solid var(--ds-color-accent-soft)",
            }}
          >
            <div className="flex gap-3">
              <Info className="w-4 h-4 mt-0.5 text-[var(--ds-color-accent)] flex-shrink-0" />
              <p className="text-xs text-[var(--ds-color-ink-muted)] leading-relaxed">{disclaimer}</p>
            </div>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

function EmptyState({ title, subtitle, primaryLabel, onPrimary, error }) {
  return (
    <section
      data-testid="partners-empty"
      className="mt-6 rounded-[var(--ds-radius-card)] p-8 md:p-10 text-center"
      style={{
        background:
          "var(--ds-color-accent-soft)",
        border: "1px solid var(--ds-color-border)",
      }}
    >
      <div
        className="inline-flex items-center justify-center w-14 h-14 rounded-[var(--ds-radius-card)] mx-auto"
        style={{
          background: "var(--ds-color-accent-soft)",
          border: "1px solid var(--ds-color-accent-soft)",
        }}
      >
        <Handshake className="w-6 h-6 text-[var(--ds-color-accent)]" />
      </div>
      <h2 className="font-semibold tracking-tight mt-5 text-[24px] md:text-[30px] font-semibold tracking-tight">{title}</h2>
      <p className="mt-3 text-sm text-[var(--ds-color-ink-muted)] max-w-xl mx-auto leading-relaxed">
        {subtitle}
      </p>
      {error && (
        <p className="mt-3 text-sm" style={{ color: "var(--ds-color-danger)" }}>{error}</p>
      )}
      <button
        type="button"
        onClick={onPrimary}
        data-testid="partners-empty-cta"
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-[var(--ds-color-accent-ink)] bg-[var(--ds-color-accent)] hover:bg-[var(--ds-color-accent-strong)] transition-all duration-300"
      >
        {primaryLabel} <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}
